import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { DEFAULT_SORT_FOR_SEARCH } from "../../../../constants/defaultSort";
import { FilterValues } from "../../../../data-model/filterValues";
import {
  processFilterData,
  transformDateFields,
  transformObjectToFacetFiltersForPostgres,
  transformObjectToKeyValueArray,
} from "../../../../utils/queryConstraint";

/**
 * Builds an Algolia-compatible search payload for My PA Orders, scoped to
 * the current user and "new" master auth status.
 *
 * @param validFilters - Currently applied filter values
 * @param searchTerm - Free-text search string
 * @param orgId - Organization ID for the query
 * @param userId - Current user's ID (used for the assigned_to filter)
 * @returns Algolia search payload with facet filters, date filters, and pagination defaults
 */
export const generateFilterPayloadForMyPaOrders = (
  validFilters: FilterValues[],
  searchTerm: string,
  orgId: string,
  userId: string,
) => {
  const filterData = processFilterData(validFilters);
  const preComputedFilterData = {
    ...filterData,
    assigned_to: [userId],
    master_auth_status: ["new"],
  };

  const createdFilterQuery = transformObjectToKeyValueArray(
    preComputedFilterData,
  ).map((item) => item);

  const dateFilterQuery = transformDateFields(preComputedFilterData);
  const payload = {
    org_id: orgId || "",
    query: searchTerm ?? "",
    facet_filters: createdFilterQuery,
    hits_per_page: 400,
    page: 0,
    ...dateFilterQuery,
    timezone_offset_minutes: new Date().getTimezoneOffset(),
  };
  return payload;
};

/**
 * Builds a Postgres-compatible search payload for My PA Orders, scoped to
 * the current user and "new" master auth status. Uses the Postgres facet
 * filter format instead of Algolia's. When a sort field is provided, it is
 * included as `sort_by` / `sort_dir` so the API returns rows in the requested order.
 *
 * @param validFilters - Currently applied filter values
 * @param searchTerm - Free-text search string
 * @param orgId - Organization ID for the query
 * @param userId - Current user's ID (used for the assigned_to filter)
 * @param sortByField - Optional canonical API field name to sort by
 * @param sortAscending - Optional sort direction; defaults to ascending when sortByField is set
 * @returns Postgres search payload with facet filters, date filters, sort params, and pagination defaults
 */
export const createFilterPayloadForMyPaOrdersForPostgres = (
  validFilters: FilterValues[],
  searchTerm: string,
  orgId: string,
  userId: string,
  sortByField?: string,
  sortAscending?: boolean,
): SearchForMedicalPaOrdersFromPostgresParams => {
  const filterData = processFilterData(validFilters);
  const preComputedFilterData = {
    ...filterData,
    assigned_to: [userId],
    master_auth_status: ["new"],
  };
  const createdFilterQuery = transformObjectToFacetFiltersForPostgres(
    preComputedFilterData,
  );
  const dateFilterQuery = transformDateFields(preComputedFilterData);
  const payload: SearchForMedicalPaOrdersFromPostgresParams = {
    org_id: orgId || "",
    query: searchTerm ?? "",
    facet_filters: createdFilterQuery,
    hits_per_page: 100,
    page: 0,
    ...dateFilterQuery,
    timezone_offset: new Date().getTimezoneOffset(),
    ...(sortByField
      ? {
          sort_by: sortByField,
          sort_dir: sortAscending === false ? "desc" : "asc",
        }
      : {}),
    default_sort: DEFAULT_SORT_FOR_SEARCH,
  };
  return payload;
};
